/**
 * Module: screens/creator/StatusScreen
 * Role: Step 8/8 — post-submit status polling (FTR-1106 + FTR-1131 chrome
 *       rewrite). Polls GET /verify_status every 30s:
 *         pending → spinner + «1–2 минуты + до 1–2 дней» copy
 *         approved → success + redirect to /tasks in 3s
 *         rejected → rejection_reason + retry button
 *         429 rate-limit (carried from wizard store) → wait-time + retry
 *       Back-pill on every non-approved branch closes the wizard to /profile
 *       (founder report 2026-06-07: any wizard surface must be escapable).
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - @tanstack/react-query:useQuery
 *   - api/creator:getVerifyStatus
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - styles/tokens:P
 *   - components/atoms/Ic
 *
 * Used by: App.tsx route /creator-status
 * Glossary: ai/glossary/buyer.md
 *
 * ADR-023: no window.Telegram / platform literals
 * ADR-022 §7: no openExternal(BOT_LINK)
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getVerifyStatus } from '../../api/creator';
import { useWizardStore } from './store';
import { P, T } from '../../styles/tokens';
import { Ic } from '../../components/atoms/Ic';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from './_chrome/WizardShell';
import { rejectionReasonLabel } from './_labels';

const POLL_INTERVAL_MS = 30_000;

function formatRetryAfter(retryAtMs: number): string {
  const diff = retryAtMs - Date.now();
  if (diff <= 0) return 'сейчас';
  const hours = Math.ceil(diff / (1000 * 60 * 60));
  if (hours >= 1) return `${hours} ${hours === 1 ? 'час' : hours < 5 ? 'часа' : 'часов'}`;
  const mins = Math.ceil(diff / (1000 * 60));
  return `${mins} ${mins === 1 ? 'минуту' : mins < 5 ? 'минуты' : 'минут'}`;
}

export default function StatusScreen() {
  const navigate = useNavigate();
  const { draft, resetDraft } = useWizardStore();

  const isRateLimited = draft.rateLimitRetryAt !== null;
  const retryAfterMs = draft.rateLimitRetryAt;

  const { data: status } = useQuery({
    queryKey: ['creator', 'verify_status'],
    queryFn: getVerifyStatus,
    refetchInterval: POLL_INTERVAL_MS,
    enabled: !isRateLimited,
  });

  useEffect(() => {
    if (status?.status === 'approved') {
      const timer = setTimeout(() => {
        resetDraft();
        navigate('/tasks', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status?.status, navigate, resetDraft]);

  const backToProfile = () => navigate('/profile');

  // ── Rate-limited (EC-5) ───────────────────────────────────────────────
  if (isRateLimited && retryAfterMs !== null) {
    const timeStr = formatRetryAfter(retryAfterMs);
    const canRetryNow = retryAfterMs <= Date.now();
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 64, height: 64, borderRadius: 32, background: 'rgba(45,37,24,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Ic name="clock" size={32} color={P.warn} />
          </div>
          <div style={{ font: T.title3, color: P.ink }}>Слишком много попыток</div>
          <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
            {canRetryNow ? 'Можно подать заявку снова' : `Попробуй снова через ${timeStr}`}
          </div>
        </WizardBody>
        {canRetryNow && (
          <WizardCtaBar
            label="Подать заново"
            onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
          />
        )}
      </WizardFrame>
    );
  }

  // ── Approved ──────────────────────────────────────────────────────────
  if (status?.status === 'approved') {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка одобрена" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 16, padding: '24px 32px' }}>
          <div
            style={{
              width: 72, height: 72, borderRadius: 36, background: '#EFF7E9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 8px rgba(45,138,78,0.10)',
            }}
          >
            <Ic name="check" size={36} color={P.success} />
          </div>
          <div style={{ font: T.title2, color: P.ink }}>Заявка одобрена!</div>
          <div style={{ font: T.subhead, color: P.inkSoft }}>Переходим к заданиям…</div>
        </WizardBody>
      </WizardFrame>
    );
  }

  // ── Rejected ──────────────────────────────────────────────────────────
  if (status?.status === 'rejected') {
    return (
      <WizardFrame>
        <WizardHeader title="Заявка" onBack={backToProfile} />
        <WizardStepStrip current={8} total={8} />
        <WizardBody>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 16 }}>
            <div
              style={{
                width: 64, height: 64, borderRadius: 32, background: 'rgba(209,67,67,0.10)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Ic name="cross" size={28} color={P.error} />
            </div>
          </div>
          <div style={{ font: T.title2, color: P.ink, textAlign: 'center' }}>
            Заявка отклонена
          </div>
          {status.rejection_reason && (
            <div
              style={{
                borderRadius: 12,
                background: P.terraSoft,
                border: `1px solid ${P.terraLight}`,
                padding: '14px 16px',
              }}
            >
              <div style={{ font: T.subhead, color: P.ink, lineHeight: 1.5 }}>
                {rejectionReasonLabel(status.rejection_reason)}
              </div>
            </div>
          )}
          <div style={{ font: T.footnote, color: P.inkSoft, textAlign: 'center' }}>
            Вы можете подать заявку повторно
          </div>
        </WizardBody>
        <WizardCtaBar
          label="Подать снова"
          onClick={() => { resetDraft(); navigate('/creator-onboarding/url', { replace: true }); }}
        />
      </WizardFrame>
    );
  }

  // ── Pending (default) ─────────────────────────────────────────────────
  return (
    <WizardFrame>
      <WizardHeader title="Заявка на проверке" onBack={backToProfile} />
      <WizardStepStrip current={8} total={8} />
      <WizardBody style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14, padding: '24px 32px' }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: '50%',
            border: `3px solid ${P.terracotta}`,
            borderTopColor: 'transparent',
            animation: 'spin 0.9s linear infinite',
          }}
        />
        <div style={{ font: T.title3, color: P.ink }}>Проверяем заявку</div>
        <div style={{ font: T.subhead, color: P.inkSoft, lineHeight: 1.5 }}>
          Обычно занимает 1–2 минуты. Если автоматическая проверка не сработает,
          заявку рассмотрят вручную — это до 1–2 рабочих дней. Мы уведомим вас
          в боте.
        </div>
      </WizardBody>
      <WizardCtaBar label="Закрыть" kind="ink" onClick={backToProfile} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </WizardFrame>
  );
}
